import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { ProjectApi } from '../../../api';
import { ProjectAssigneeRateRowDto } from '../../../commonTypes/project.types';
import { useContainer } from '../../useContainer';

const DEFAULT_HISTORY_START = '2000-01-01';

function historyRangeFromProjectStart(projectStartDate?: string): string {
    if (!projectStartDate) return DEFAULT_HISTORY_START;
    const d = DateTime.fromISO(projectStartDate);
    return d.isValid ? d.toISODate()! : DEFAULT_HISTORY_START;
}

const useHttpPostProjectAssigneeRateHistory = (projectId: string, enabled: boolean, projectStartDate: string | undefined, revision: number = 0) => {
    const projectApi = useContainer(ProjectApi);
    const [rows, setRows] = useState<ProjectAssigneeRateRowDto[]>([]);
    const [isReady, setIsReady] = useState(() => !enabled);

    const fromDate = useMemo(() => historyRangeFromProjectStart(projectStartDate), [projectStartDate]);

    useEffect(() => {
        if (!enabled) {
            setIsReady(true);
            setRows([]);
            return;
        }
        const toDate = DateTime.now().toISODate();
        if (!projectId || !fromDate || !toDate) {
            setIsReady(true);
            setRows([]);
            return;
        }

        setIsReady(false);
        const { exec, abort } = projectApi.postProjectAssigneesRateForPeriod({
            body: { from: fromDate, to: toDate, projectId },
            onSuccess: res => setRows(res.body ?? []),
            onError: () => setRows([]),
            onFinally: () => setIsReady(true),
        });
        exec();
        return abort;
    }, [enabled, projectId, fromDate, revision, projectApi]);

    return { rows, isReady, fromDate };
};

export default useHttpPostProjectAssigneeRateHistory;
